import { useState, useEffect, useRef, createRef, memo } from "react";

import {
  Constants,
  useParticipant,
  useMeeting,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { toast } from "react-toastify";
import { gettext } from '@resuelve/malinchex';

import { SidebarConatiner } from "../components/sidebar/SidebarContainer";
import { PresenterView } from "../components/PresenterView";
import { nameTructed, trimSnackBarText } from "../utils/helper";
import { ILSBottomBar } from "./components/ILSBottomBar";
import { TopBar } from "./components/TopBar";
import useIsTab from "../hooks/useIsTab";
import PollsListner from "./components/pollContainer/PollListner";
import FlyingEmojisOverlay from "./components/FlyingEmojisOverlay";
import MemorizedILSParticipantView from "./components/ILSParticipantView";
import ConfirmBox from "../components/ConfirmBox";
import useIsMobile from "../hooks/useIsMobile";
import { useMediaQuery } from "react-responsive";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import WaitingToJoinScreen from "../components/screens/WaitingToJoinScreen";
import { i18nTexts } from "../i18n";
import { AUDIO_URLS } from "../utils/constants";

export function ILSContainer({
  onMeetingLeave,
  bravoSiteUrl
}) {
  const {
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    useRaisedHandParticipants
  } = useMeetingAppContext();
  const [participantsData, setParticipantsData] = useState([]);
  const ParticipantMicStream = memo(({ participantId }) => {
    // Individual hook for each participant
    const { micStream, isLocal } = useParticipant(participantId);

    useEffect(() => {
      let audioElement;
      if (micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);

        audioElement = new Audio();
        audioElement.srcObject = mediaStream;
        audioElement.muted = isLocal;
        audioElement.play();
      }

      return () => {
        if (audioElement) {
          audioElement.pause();
          audioElement.srcObject = null;
        }
      };
    }, [micStream, participantId]);

    return null;
  }, (prevProps, nextProps) => prevProps.participantId === nextProps.participantId);

  const bottomBarHeight = 60;
  const topBarHeight = 60;
  const bucketUrl = process.env.REACT_APP_S3_BUCKET_URL;

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] = useState(null);
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);

  const mMeetingRef = useRef();
  const containerRef = createRef();
  const containerHeightRef = useRef();
  const containerWidthRef = useRef();

  useEffect(() => {
    containerHeightRef.current = containerHeight;
    containerWidthRef.current = containerWidth;
  }, [containerHeight, containerWidth]);

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const extraVerticalOffset =
    isLGDesktop
      ? 85
      : isXLDesktop
        ? 85
        : 0

  const sideBarContainerWidth = isXLDesktop
    ? 400
    : isLGDesktop
      ? 360
      : isTab
        ? 320
        : isMobile
          ? 280
          : 240;

  useEffect(() => {
    containerRef.current?.offsetHeight &&
      setContainerHeight(containerRef.current.offsetHeight);
    containerRef.current?.offsetWidth &&
      setContainerWidth(containerRef.current.offsetWidth);

    window.addEventListener("resize", ({ target }) => {
      containerRef.current?.offsetHeight &&
        setContainerHeight(containerRef.current.offsetHeight);
      containerRef.current?.offsetWidth &&
        setContainerWidth(containerRef.current.offsetWidth);
    });
  }, [containerRef]);

  const { participantRaisedHand } = useRaisedHandParticipants();

  const _handleOnRecordingStateChanged = ({ status }) => {
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      toast(
        `${status === Constants.recordingEvents.RECORDING_STARTED
          ? gettext(i18nTexts.meeting_screen.views.recording.notify.start)
          : gettext(i18nTexts.meeting_screen.views.recording.notify.stop)
        }`,
        {
          position: "bottom-left",
          autoClose: 4000,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
    }
  };

  const onParticipantJoined = (participant) => {
    // Change quality to low, med or high based on resolution
    participant && participant.setQuality("high");
  }

  const onEntryResponded = (participantId, name) => {
    if (mMeetingRef.current?.localParticipant?.id === participantId) {
      if (name === "allowed") {
        setLocalParticipantAllowedJoin(true);
      } else {
        setLocalParticipantAllowedJoin(false);
        setTimeout(() => {
          onMeetingLeave();
        }, 3000);
      }
    }
  }

  const onMeetingJoined = () => {
    console.log("onMeetingJoined");
  }

  function onMeetingLeft() {
    const defaultParams = { id: null, label: null };

    setSelectedMic(defaultParams)
    setSelectedWebcam(defaultParams)
    setSelectedSpeaker(defaultParams)
    onMeetingLeave();
  }

  const _handleOnError = (data) => {
    const { code, message } = data;

    const joiningErrCodes = [
      4001, 4002, 4003, 4004, 4005, 4006, 4007, 4008, 4009, 4010,
    ];

    const isJoiningError = joiningErrCodes.findIndex((c) => c === code) !== -1;
    const isCriticalError = `${code}`.startsWith("500");

    new Audio(
      isCriticalError
        ? AUDIO_URLS.CRITICAL_ERROR
        : AUDIO_URLS.ERROR
    ).play();

    setMeetingErrorVisible(true);
    setMeetingError({
      code,
      message: isJoiningError ? "Unable to join meeting!" : message,
    });
  };

  const mMeeting = useMeeting({
    onParticipantJoined,
    onEntryResponded,
    onMeetingJoined,
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
  });

  const isPresenting = mMeeting.presenterId ? true : false;

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const participantIds = Array.from(mMeeting.participants.keys());
      console.log("Debounced participantIds", participantIds);

      setParticipantsData(participantIds);
      console.log("Setting participants");
    }, 500);


    return () => clearTimeout(debounceTimeout);
  }, [mMeeting.participants]);


  useEffect(() => {
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName } = data;

      const isLocal = senderId === localParticipantId;

      new Audio(AUDIO_URLS.NOTIFICATION).play();

      toast(`${isLocal ? "You" : nameTructed(senderName, 15)} raised hand 🖐🏼`, {
        position: "bottom-left",
        autoClose: 4000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      participantRaisedHand(senderId);
    },
  });

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName, message } = data;

      const isLocal = senderId === localParticipantId;

      if (!isLocal) {
        new Audio(AUDIO_URLS.NOTIFICATION).play();

        toast(
          `${trimSnackBarText(
            `${nameTructed(senderName, 15)} says: ${message}`
          )}`,
          {
            position: "bottom-left",
            autoClose: 4000,
            hideProgressBar: true,
            closeButton: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          }
        );
      }
    },
  });

  return (
    <div className="fixed inset-0">
      <div ref={containerRef} className="h-full flex flex-col bg-gray-800">
        <FlyingEmojisOverlay />
        {typeof localParticipantAllowedJoin === "boolean" ? (
          localParticipantAllowedJoin ? (
            <>
              <PollsListner />
              <header className="w-full py-4 bg-purple-900 flex items-center justify-center">
                <img
                  src={`${bucketUrl}/logotipo2_e6be46630f.png`}
                  alt="Logo"
                  className="mt-2 mb-2"
                />
              </header>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: topBarHeight,
                }}
              >
                <TopBar topBarHeight={topBarHeight} />
              </div>
              <div className={`flex flex-1 flex-row bg-gray-800`}>
                <div className={`flex flex-1`}>
                  {isPresenting ? (
                    <PresenterView
                      height={containerHeight - topBarHeight - bottomBarHeight}
                    />
                  ) : null}
                  {isPresenting && isMobile ? (
                    participantsData.map((participantId) => (
                      <ParticipantMicStream key={participantId} participantId={participantId} />
                    ))
                  ) : (
                    <MemorizedILSParticipantView isPresenting={isPresenting} />
                  )}
                </div>

                <SidebarConatiner
                  height={
                    isMobile || isTab
                      ? containerHeight - bottomBarHeight
                      : containerHeight - topBarHeight - bottomBarHeight - extraVerticalOffset
                  }
                  sideBarContainerWidth={sideBarContainerWidth}
                />
              </div>

              <ILSBottomBar
                bottomBarHeight={bottomBarHeight}
                setIsMeetingLeft={onMeetingLeft}
              />
            </>
          ) : (
            <></>
          )
        ) : (
          !mMeeting.isMeetingJoined && <WaitingToJoinScreen timeoutMs={10000} bravoSiteUrl={bravoSiteUrl} />
        )}
        <ConfirmBox
          open={meetingErrorVisible}
          successText={gettext(i18nTexts.meeting_screen.views.error.success_text)}
          onSuccess={() => {
            setMeetingErrorVisible(false);
          }}
          title={`${gettext(i18nTexts.meeting_screen.views.error.title)}: ${meetingError.code}`}
          subTitle={meetingError.message}
        />
      </div>
    </div>
  );
}
